<template>
  <div class="entry-edit">
    <div class="header">
      <h2>{{ t('excel.edit.title') }}</h2>
      <router-link to="/excelEntryManager">
        <el-button>{{ t('common.backToList') }}</el-button>
      </router-link>
    </div>

    <el-card v-if="entry" class="entry-card">
      <template #header>
        <div class="card-header">
          <div class="main-info">
            <span class="info-item">
              <strong>{{ t('excel.fields.id') }}:</strong> 
              <span class="id-value">{{ entry.id }}</span>
            </span>
            <span class="info-item">
              <strong>{{ t('excel.fields.date') }}:</strong> 
              {{ new Date(entry.dt).toLocaleDateString() }}
            </span>
            <span class="info-item">
              <strong>{{ t('excel.fields.value') }}:</strong> 
              {{ new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(entry.valor) }}
            </span>
          </div>
          <div class="last-changed">
            {{ t('excel.fields.lastModified') }}: {{ new Date(entry.lastchanged).toLocaleString() }}
          </div>
        </div>
      </template>

      <el-form :model="entry" label-width="120px">
        <el-form-item :label="t('excel.fields.description')">
          <div class="description-input-group">
            <el-radio-group v-model="inputMode" size="small" class="input-mode-toggle" style="margin-bottom: 8px;">
              <el-radio-button value="text">{{ t('excel.fields.newDescription') }}</el-radio-button>
              <el-radio-button value="select">{{ t('excel.fields.existingDescription') }}</el-radio-button>
            </el-radio-group>
            
            <el-input
              v-if="inputMode === 'text'"
              v-model="entry.descricao"
              :maxlength="500"
              show-word-limit
              clearable
              style="width: 100%"
            />
            
            <el-select
              v-else
              v-model="entry.descricao"
              filterable
              clearable
              style="width: 100%"
              :placeholder="t('excel.fields.selectDescription')"
            >
              <el-option
                v-for="desc in descriptions"
                :key="desc"
                :label="desc"
                :value="desc"
              />
            </el-select>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="saveEntry" :loading="isSaving">
            {{ t('common.save') }}
          </el-button>
          <router-link to="/excelEntryManager">
            <el-button>{{ t('common.cancel') }}</el-button>
          </router-link>
        </el-form-item>
      </el-form>
    </el-card>

    <excel-entry-history 
      v-if="savedDescription"
      :descricao="savedDescription"
    />

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import type { ExcelEntryDTO } from '../types/accounting'
import Swal from 'sweetalert2'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import ExcelEntryHistory from './ExcelEntryHistory.vue'

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

const entry = ref<ExcelEntryDTO | null>(null)
const error = ref<string | null>(null)
const isSaving = ref(false)
const descriptions = ref<string[]>([])
const inputMode = ref<'text' | 'select'>('text')
const savedDescription = ref<string>('')

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + authStore.keycloak?.token
  }
})

const fetchDescriptions = async () => {
  try {
    const response = await api.get<string[]>(
      `${import.meta.env.VITE_EXCEL_API_URL}/excel/descricoes`
    )
    Swal.fire({
      title: 'Success',
      text: 'Descriptions fetched successfully (' + response.data.length + ')',
      icon: 'success'
    })
    descriptions.value = response.data
  } catch (err) {
    console.error('Failed to fetch descriptions:', err)
  }
}

const fetchEntry = async () => {
  try {
    const response = await api.get<ExcelEntryDTO>(
      `${import.meta.env.VITE_EXCEL_API_URL}/excel/${route.params.id}`
    )
    entry.value = response.data
    savedDescription.value = response.data.descricao
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Failed to load entry'
    Swal.fire({
      title: 'Error',
      text: 'Failed to load entry: ' + error.value,
      icon: 'error'
    })
  }
}

const saveEntry = async () => {
  if (!entry.value) return

  isSaving.value = true
  try {
    const response =  await api.post(
      `${import.meta.env.VITE_EXCEL_API_URL}/excel/saveDescricao/${entry.value.id}`,
      { descricao: entry.value.descricao }
    )
    entry.value = response.data
    savedDescription.value = response.data.descricao
    Swal.fire({
      title: 'Success',
      text: 'Entry updated successfully',
      icon: 'success'
    })

  } catch (err: any) {
    error.value = err.response?.data?.message || 'Failed to save entry'
    Swal.fire({
      title: 'Error',
      text: 'Failed to save entry: ' + error.value,
      icon: 'error'
    })
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  await Promise.all([
    fetchEntry(),
    fetchDescriptions()
  ])
})
</script>

<style scoped>
.entry-edit {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.entry-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.main-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.id-value {
  font-family: monospace;
  font-size: 1.1em;
}

.last-changed {
  font-size: 0.85em;
  color: #909399;
  text-align: right;
}

.description-input-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-mode-toggle {
  align-self: flex-start;
}

.description-select {
  width: 100%;
}

.el-form-item {
  margin-bottom: 25px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style> 